import { ZuiTableBaseElement } from './base.js';
import { html } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { style } from './zui-table-row-css.js';
import type { ZuiTableCellElement } from './zui-table-cell.js';

/**
 * Insert into `<zui-table>`; `<zui-table-row>` holds a row of `<zui-table-cell>` elements.
 *
 * @element zui-table-row
 *
 * @slot - Default, unnamed slot; for inserting `<zui-table-cell>` elements into `<zui-table-row>`
 *
 * @cssprop [--zui-table-columns-template=repeat(auto-fit, minmax(0, 1fr))] - Override the table columns template. See https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns for more information on valid values.
 * @cssprop [--zui-table-summary-background-color=var(--zui-gray-600)] - Override the table summary background color
 * @cssprop [--zui-table-summary-text-color=#fff] - Override the table summary text color
 */
export class ZuiTableRowElement extends ZuiTableBaseElement {
  /**
   * Declare a table header; typically the first row in `<zui-table>`
   */
  @property({ type: Boolean, reflect: true })
  header = false;

  /**
   * Declare a table summary row; typically the last row in `<zui-table>` before `<zui-table-footer>`
   */
  @property({ type: Boolean, reflect: true })
  summary = false;

  @queryAssignedElements({ selector: 'zui-table-cell' })
  private _slottedCells: Array<ZuiTableCellElement>;

  #sortLock?: boolean;

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'row');
  }

  firstUpdated() {
    if (this.header) {
      const firstCellWithDirection = this._slottedCells?.find(
        (cell) => cell?.hasAttribute('sort') || cell?.sort !== null
      );
      this._slottedCells.forEach((cell) => {
        if ((cell?.hasAttribute('sort') || cell.sort) && cell !== firstCellWithDirection) {
          cell.sort = null;
          cell.removeAttribute('sort');
        }
      });
    }
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('header')) {
      this.#ensureCellState();
      if (this.header) {
        this._state?.channel.addEventListener('sort', (e: CustomEvent<{ cell: HTMLElement }>) => this.#onTableSort(e));
      }
    }
  }

  render() {
    return html`<div>
      <slot @slotchange="${this.#ensureCellState()}"></slot>
    </div>`;
  }

  #onTableSort(event: CustomEvent<{ cell: HTMLElement }>) {
    if (this.#sortLock) {
      return;
    }
    this.#sortLock = true;
    this._slottedCells?.forEach((cell) => {
      if (cell !== event.detail.cell) {
        cell.sort = null;
      }
    });
    setTimeout(() => {
      this.#sortLock = false;
    }, 0);
  }

  #ensureCellState() {
    if (!this._slottedCells) {
      return;
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */

    if (this.header) {
      this._slottedCells.forEach((cell) => {
        cell.setAttribute('role', 'columnheader');
        (cell as any)._isAllowedSort = true;
      });
    } else {
      this._slottedCells.forEach((cell) => {
        cell.setAttribute('role', 'cell');
        (cell as any)._isAllowedSort = false;
      });
    }

    /* eslint-enable @typescript-eslint/no-explicit-any */
  }
}

window.customElements.define('zui-table-row', ZuiTableRowElement);

declare global {
  interface HTMLElementTagNameMap {
    'zui-table-row': ZuiTableRowElement;
  }
}
